import { Alert, Box, IconButton, InputAdornment, List, Stack, TextField, Typography } from "@mui/material"
import { useMemo, useState } from "react"
import { DialogBox } from "lib/@components/dialogBox"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"
import { ScheduleListRow } from "./schedule-list-row"

export function DeleteScopedSchedulesDialog({ selectedSchedules = [] }) {
    const [search, setSearch] = useState("")
    const filteredSelectedSchedules = useMemo(filterSelectedSchedules, [selectedSchedules, search])
    const selectedCount = selectedSchedules.length
    const title = getDialogTitle()
    const message = getDialogMessage()
    const guidance = getDialogGuidance()
    const countSummary = getCountSummary()

    return (
        <DialogBox
            title={<Typography variant="h5">{title}</Typography>}
            value={selectedSchedules.map(({ id }) => id)}
            okColor="error"
            okCaption="Remove"
            okIcon="gravity-ui:trash-bin"
            cancelColor="text"
            data-cy="delete-dialog"
            sx={{
                "& .MuiDialogContent-root": {
                    overflow: "hidden",
                },
            }}
        >
            <Stack spacing={2}>
                <Alert
                    severity="warning"
                    icon={<Iconify icon={"material-symbols:warning-rounded"} />}
                    sx={{
                        alignItems: "flex-start",
                        "& .MuiAlert-icon": { display: "flex", alignItems: "center", mt: 0.25 },
                    }}
                >
                    <Stack spacing={1}>
                        <Typography variant="subtitle1" data-cy="delete-schedules-message">
                            {message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {guidance}
                        </Typography>
                    </Stack>
                </Alert>
                <If truthy={selectedCount > 0}>
                    <Stack spacing={2}>
                        <If truthy={selectedCount > 1}>
                            <TextField
                                data-cy="delete-schedules-search"
                                placeholder="Search selected schedules"
                                value={search}
                                onChange={handleSearchChange}
                                size="small"
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="eva:search-fill" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="Clear search"
                                                    edge="end"
                                                    size="small"
                                                    onClick={clearSearch}
                                                    disabled={!search}
                                                >
                                                    <Iconify icon="material-symbols:close-rounded" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </If>
                        <Typography variant="body2" color="text.secondary" data-cy="delete-schedules-count">
                            {countSummary}
                        </Typography>
                        <List
                            data-cy="delete-schedules-list"
                            disablePadding
                            sx={{
                                maxHeight: "32vh",
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                overscrollBehavior: "contain",
                            }}
                        >
                            <Repeat
                                list={filteredSelectedSchedules}
                                item={<ScheduleListRow data-cy="delete-schedule-row" />}
                                pass="schedule"
                                fallback={
                                    <Box px={2} py={3}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            data-cy="delete-schedules-empty"
                                        >
                                            No selected schedules match your search.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </List>
                    </Stack>
                </If>
            </Stack>
        </DialogBox>
    )

    function filterSelectedSchedules() {
        const searchTerm = search.trim().toLowerCase()
        if (!searchTerm) return selectedSchedules

        return selectedSchedules.filter(matchesSearchTerm)

        function matchesSearchTerm({ code, title, status, isDraft }) {
            const searchableStatus = isDraft ? status : "no draft"
            return [code, title, searchableStatus].some((value) => value?.toLowerCase?.().includes(searchTerm))
        }
    }

    function handleSearchChange({ target }) {
        setSearch(target.value)
    }

    function clearSearch() {
        setSearch("")
    }

    function getDialogTitle() {
        if (selectedCount === 1) return "Remove schedule"
        return `Remove ${selectedCount} schedules`
    }

    function getDialogMessage() {
        if (selectedCount === 1)
            return "Are you sure you want to permanently remove this schedule from this work package?"
        return "Are you sure you want to permanently remove these schedules from this work package?"
    }

    function getDialogGuidance() {
        if (selectedCount === 1)
            return "Review the selected schedule below before confirming. This action cannot be undone."
        return "Review the selected schedules below before confirming. This action cannot be undone."
    }

    function getCountSummary() {
        if (selectedCount === 1) return "1 schedule selected for removal."
        return `${selectedCount} schedules selected for removal.`
    }
}
